import { apiRequestErrorFromResponse } from './error-reporting';
function apiBaseUrl(): string {
    const runtimeConfig = globalThis as typeof globalThis & {
        __DFL_FRANCHISE_API_BASE_URL__?: string;
        location?: Location;
    };
    const configuredBaseUrl = runtimeConfig.__DFL_FRANCHISE_API_BASE_URL__?.trim();
    if (configuredBaseUrl) {
        return configuredBaseUrl.replace(/\/+$/, '');
    }

    const hostname = runtimeConfig.location?.hostname || '';
    if (hostname === 'dflplaymaker.com' || hostname.endsWith('.dflplaymaker.com') || hostname.endsWith('.amplifyapp.com')) {
        return 'https://api.dflplaymaker.com';
    }

    return 'http://localhost:3001';
}

async function readJson<T>(response: Response): Promise<T> {
    if (!response.ok) {
        throw await apiRequestErrorFromResponse(response);
    }

    return response.json() as Promise<T>;
}

export type ReseedSummary = {
    ok: boolean;
    /** Total items deleted across every table before reseeding. */
    cleared: number;
    /** Map of domain label -> records seeded. */
    seeded: Record<string, number>;
    /** Whether the simulator was successfully told to re-pull the reseeded data. */
    simulator?: { ok: boolean; error?: string };
};

type ReseedStatusResponse = {
    ok: boolean;
    run: {
        running: boolean;
        startedAt: string;
        finishedAt?: string;
        ok?: boolean;
        cleared?: number;
        seeded?: Record<string, number>;
        simulator?: { ok: boolean; error?: string };
        error?: string;
    } | null;
};

const RESEED_POLL_INTERVAL_MS = 3_000;
const RESEED_POLL_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * Wipe every DynamoDB table and reseed (franchise registry from the hosted
 * metadata catalog, remaining domains from the committed JSON). Requires an
 * admin wallet session token — the server re-checks its wallet allowlist and
 * rejects non-admin callers with 403.
 *
 * The server runs the reseed in the background (clearing and rewriting
 * thousands of items outlasts load-balancer timeouts), so this starts the run
 * and polls the status endpoint until it finishes, resolving with the final
 * summary exactly as the old synchronous call did.
 */
export async function reseedLeagueData(walletSessionToken: string): Promise<ReseedSummary> {
    const headers = { Authorization: `Bearer ${walletSessionToken}` };
    const started = await fetch(`${apiBaseUrl()}/api/admin/reseed`, { method: 'POST', headers });
    await readJson<{ ok: boolean; startedAt: string }>(started);

    const deadline = Date.now() + RESEED_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, RESEED_POLL_INTERVAL_MS));
        const response = await fetch(`${apiBaseUrl()}/api/admin/reseed-status`, { method: 'POST', headers });
        const { run } = await readJson<ReseedStatusResponse>(response);
        if (!run || run.running) {
            continue;
        }
        if (run.ok === false) {
            throw new Error(run.error || 'Reseed failed.');
        }
        return {
            ok: true,
            cleared: run.cleared ?? 0,
            seeded: run.seeded ?? {},
            simulator: run.simulator,
        };
    }
    throw new Error('Timed out waiting for the reseed to finish; check the reseed status again shortly.');
}

export type PlayerOwnerSyncSummary = {
    ok: boolean;
    /** Player NFTs with a current owner in the refreshed snapshot. */
    players: number;
    /** Team NFTs with a current owner in the refreshed snapshot. */
    teams: number;
    updatedAt: string;
    /** Franchise ids whose depth charts lost players during reconciliation. */
    reconciledFranchises: string[];
    /** Total player-pool records scanned. */
    poolPlayers: number;
    /** Pool records whose ownerWallet was set or changed. */
    ownersAssigned: number;
    /** Pool records whose stale ownerWallet was removed. */
    ownersCleared: number;
};

/**
 * Fetch the current on-chain owner of every player NFT and save it onto the
 * player-pool records (`ownerWallet`). Requires an admin wallet session token —
 * the server re-checks its wallet allowlist before running.
 */
export async function syncPlayerOwners(walletSessionToken: string): Promise<PlayerOwnerSyncSummary> {
    const response = await fetch(`${apiBaseUrl()}/api/admin/sync-player-owners`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${walletSessionToken}`,
        },
    });
    return readJson<PlayerOwnerSyncSummary>(response);
}

export type SlateTimingAdminOptions = {
    /** UTC hour of each slate's first kickoff (default 24 = 7:00 PM Central; values past 23 roll into the next UTC day). */
    firstKickoffUtcHour?: number;
    /** Minutes between consecutive kickoffs in a slate. Default 10. */
    kickoffIntervalMinutes?: number;
    /** Number of games sharing each kickoff time. Default 1. */
    gamesPerKickoffSlot?: number;
    /** Max games on one calendar day; larger slates spill onto following days. Default: whole slate on one day. */
    gamesPerDay?: number;
    /** Minutes added to every kickoff. Default 0. */
    kickoffOffsetMinutes?: number;
    /** Per-game broadcast pacing overrides (milliseconds). */
    broadcast?: {
        introMs?: number;
        playMs?: number;
        betweenPlayMs?: number;
        finalHoldMs?: number;
    };
};

export type CreateSeasonAdminRequest = {
    seasonId: string;
    seasonName?: string;
    startDate?: string;
    slateIntervalDays?: number;
    /** Kickoff-timing knobs applied to every league; omitted fields keep the defaults. */
    timing?: SlateTimingAdminOptions;
    /** Minutes between consecutive leagues' first kickoffs. Default 0 (leagues share kickoff times). */
    leagueStaggerMinutes?: number;
    replaceExisting?: boolean;
    shuffleSeed?: number;
};

export type CreateSeasonAdminResult = {
    ok: boolean;
    seasonId: string;
    seasonName: string;
    leagueCount: number;
    teamCount: number;
    mintedCount: number;
    mintedUsed: number;
    paddedCount: number;
    shuffleSeed: number;
    leagues: Array<{ leagueId: string; leagueName: string; teamIds: string[]; gamesCreated: number; weeks: number }>;
    gamesCreated: number;
    gamesDeleted: number;
};

/**
 * Create a production season from the minted teams: every minted catalog team
 * makes the draw, the league count is the minimum number of 32-team leagues
 * that fits them all, unminted teams pad the remaining slots, the pool is
 * shuffled with a recorded seed, and each 32-team slice becomes an
 * NFL-structure league with its own schedule and playoff bracket. Requires an
 * admin wallet session.
 */
export async function createSeasonFromMintedTeams(
    walletSessionToken: string,
    request: CreateSeasonAdminRequest,
): Promise<CreateSeasonAdminResult> {
    const response = await fetch(`${apiBaseUrl()}/api/admin/create-season`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${walletSessionToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });
    return readJson<CreateSeasonAdminResult>(response);
}

export type CreateSeasonThreeAdminResult = {
    ok: boolean;
    seasonId: string;
    seasonName: string;
    leagueId: string;
    teamCount: number;
    regularSeasonGames: number;
    playoffGames: number;
    gamesCreated: number;
    gamesDeleted: number;
};

/** Create the fixed NFT 13-36 Season 3 calendar. Requires an admin session. */
export async function createSeasonThree(
    walletSessionToken: string,
    replaceExisting = false,
): Promise<CreateSeasonThreeAdminResult> {
    const response = await fetch(`${apiBaseUrl()}/api/admin/create-season-three`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${walletSessionToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ replaceExisting }),
    });
    return readJson<CreateSeasonThreeAdminResult>(response);
}

export type CreateSeasonFourAdminResult = CreateSeasonThreeAdminResult & { preseasonGames: number };

/** Create the fixed 16-team Season 4 calendar. Requires explicit dates and an admin session. */
export async function createSeasonFour(
    walletSessionToken: string,
    request: { startDate?: string; playoffStartDate?: string; replaceExisting?: boolean } = {},
): Promise<CreateSeasonFourAdminResult> {
    const response = await fetch(`${apiBaseUrl()}/api/admin/create-season-four`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${walletSessionToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });
    return readJson<CreateSeasonFourAdminResult>(response);
}

export type SeedSeasonThreeRostersAdminResult = {
    ok: boolean;
    players: number;
    depthCharts: number;
    branding: number;
    simulator: { ok: boolean; error?: string };
};

/** Upsert Season 3's spoofed rosters, depth charts, and coordinated team branding. */
export async function seedSeasonThreeRosters(
    walletSessionToken: string,
): Promise<SeedSeasonThreeRostersAdminResult> {
    const response = await fetch(`${apiBaseUrl()}/api/admin/seed-season-three-rosters`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${walletSessionToken}` },
    });
    return readJson<SeedSeasonThreeRostersAdminResult>(response);
}

export type CreateCustomLeagueAdminRequest = {
    seasonId: string;
    leagueId: string;
    leagueName?: string;
    teamIds: string[];
    structure: 'nfl' | 'divisions' | 'round-robin';
    startDate?: string;
    slateIntervalDays?: number;
    /** Kickoff-timing knobs; omitted fields keep the defaults. */
    timing?: SlateTimingAdminOptions;
    replaceExisting?: boolean;
};

export type CreateCustomLeagueAdminResult = {
    ok: boolean;
    seasonId: string;
    leagueId: string;
    leagueName: string;
    structure: 'nfl' | 'divisions' | 'round-robin';
    teamCount: number;
    weeks: number;
    gamesCreated: number;
    gamesDeleted: number;
};

/**
 * Create an admin-curated league (8–32 hand-picked teams) inside a season, with
 * the chosen schedule structure, plus its playoff bracket. Requires an admin
 * wallet session.
 */
export async function createCustomLeague(
    walletSessionToken: string,
    request: CreateCustomLeagueAdminRequest,
): Promise<CreateCustomLeagueAdminResult> {
    const response = await fetch(`${apiBaseUrl()}/api/admin/create-custom-league`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${walletSessionToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
    });
    return readJson<CreateCustomLeagueAdminResult>(response);
}
